import React, { useCallback, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'reactstrap';
import { noop } from 'lodash-es';
import { SOURCES } from 'client/utils/image-helpers';
import { getNoVehicleImageToDisplay } from 'site-modules/shared/utils/get-no-vehicle-image-to-display';
import { buildOverviewItemId } from 'site-modules/personalized-search/utils/build-overview-item-id';
import { OverviewContext } from 'site-modules/personalized-search/context/overview-context/overview-context';
import { Image } from 'site-modules/shared/components/image/image';
import { EdmundsTested } from 'site-modules/personalized-search/components/header-content/edmunds-tested';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { TooltipItem } from 'site-modules/shared/components/tooltip-item/tooltip-item';
import { useSafeId } from 'site-modules/shared/hooks/use-safe-id';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { PHOTO_TYPE } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { isUsed } from 'site-modules/shared/utils/publication-states';
import { HeaderCoreLink } from 'site-modules/personalized-search/components/header-content/header-core-link';
import { logger } from 'client/utils/logger/logger';

import './header-content.scss';

const tooltipText = 'Price range reflects Base MSRP for various trim levels, not including options or fees';
const ratingTooltipText = `The Edmunds Vehicle Testing Team evaluates a fresh batch of vehicles every week, pairing objective
    assessments at our test track with real-world driving on city streets, freeways and winding roads. The
    data we gather results in our Expert Ratings. They’re based on 30-plus scores that cover every aspect
    of the automotive experience.`;

export function HeaderContent({
  vehicleTitleTag: VehicleTitleTag = 'h2',
  make,
  model,
  trim,
  makeNiceName,
  modelNiceName,
  year,
  photoUrl = '',
  vehiclePrice,
  estimatedPrice,
  editorialRating,
  withAnchorLink = false,
  pubStates = {},
  creativeId,
  onRendered = noop,
  trackActionStart = false,
  actionStartTrackingData,
}) {
  const { toggleItem } = useContext(OverviewContext);
  const editorialRatingId = `-rating-${useSafeId()}`;
  const id = buildOverviewItemId({
    year,
    make: { value: makeNiceName },
    model: { value: modelNiceName },
    trim: { name: trim },
  });

  const onLinkClick = useCallback(() => {
    toggleItem(id, true);
  }, [id, toggleItem]);

  const isUsedType = isUsed(pubStates);

  useEffect(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_LIST,
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
        creative_id: 'photoflipper',
        photo_type: PHOTO_TYPE.OEM,
      },
    });
  }, []);

  useEffect(() => {
    onRendered();
  }, [onRendered]);

  useEffect(() => {
    if (trackActionStart && actionStartTrackingData) {
      EventToolbox.fireTrackAction({
        ...actionStartTrackingData,
        event_type: TrackingConstant.EVENT_TYPE_ACTION_START,
        event_data: {},
      });
      EventToolbox.fireTrackAction({
        ...Object.fromEntries(Object.entries(actionStartTrackingData).map(([key]) => [key, undefined])),
      });
    }
  }, [actionStartTrackingData, trackActionStart]);

  const onTooltipOpen = useCallback(
    ({ id: tooltipId }) => {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_VIEW_CONTENT,
          subaction_name: TrackingConstant.ACTION_VIEW_CONTENT,
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
          creative_id: creativeId,
          value: tooltipId.startsWith('-rating') ? 'rating tooltip' : 'pricing',
        },
      });
    },
    [creativeId]
  );

  const onError = useCallback(() => {
    logger.warn('Overview header image error', null);
  }, []);

  return (
    <Row className="w-100 g-0 d-flex flex-nowrap" data-tracking-parent={creativeId}>
      <Col xs="auto" className="header-photo-wrapper">
        <HeaderCoreLink
          dataTrackingId="select_photos"
          makeNiceName={makeNiceName}
          modelNiceName={modelNiceName}
          make={make}
          model={model}
          year={year}
          pubStates={pubStates}
        >
          <Image
            className="w-100 h-100 rounded-12"
            errorImage={getNoVehicleImageToDisplay()}
            alt=""
            imageId={photoUrl}
            source={SOURCES.EDMUNDSCDN}
            onError={onError}
          />
        </HeaderCoreLink>
      </Col>
      <Col md={6} className="ps-1 text-cool-gray-30 size-16 col">
        {editorialRating && <EdmundsTested className="mb-0_5" />}

        <VehicleTitleTag className="size-20 fw-bold text-cool-gray-10 mb-0_25 text-decoration-underline">
          <HeaderCoreLink
            dataTrackingId="view_review"
            dataTrackingValue={`${year} ${make} ${model}`}
            makeNiceName={makeNiceName}
            modelNiceName={modelNiceName}
            make={make}
            model={model}
            year={year}
            pubStates={pubStates}
          >
            {year} {make} {model}
            {trim && ` ${trim}`}
          </HeaderCoreLink>
        </VehicleTitleTag>

        {vehiclePrice && (
          <div>
            <span>{vehiclePrice}</span>
            {!isUsedType && (
              <TooltipItem
                id={`-header-${makeNiceName}-${modelNiceName}-${year}`}
                item={{ text: tooltipText, placement: 'bottom' }}
                className="tooltip-z-index text-gray-darker d-inline-flex align-self-center top-0"
                iconClassName="px-0_25"
                screenReaderTooltipLabel={vehiclePrice}
                onTooltipOpen={onTooltipOpen}
              />
            )}
          </div>
        )}
        {estimatedPrice && <div>est. starting price {estimatedPrice}</div>}
        {editorialRating && (
          <div className="text-capitalize">
            {editorialRating}
            <TooltipItem
              id={editorialRatingId}
              className="tooltip-z-index text-gray-darker d-inline-flex align-self-center top-0"
              iconClassName="px-0_25"
              item={{ placement: 'bottom', text: ratingTooltipText }}
              onTooltipOpen={onTooltipOpen}
            />
          </div>
        )}
        {withAnchorLink && (
          <ScrollLink
            to={id}
            onClick={onLinkClick}
            className="d-inline-block text-underline mt-0_5"
            data-tracking-id="view_overview"
          >
            Get an overview
          </ScrollLink>
        )}
      </Col>
    </Row>
  );
}

HeaderContent.propTypes = {
  vehicleTitleTag: PropTypes.string,
  make: PropTypes.string,
  model: PropTypes.string,
  trim: PropTypes.string,
  makeNiceName: PropTypes.string,
  modelNiceName: PropTypes.string,
  year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  photoUrl: PropTypes.string,
  vehiclePrice: PropTypes.string,
  estimatedPrice: PropTypes.string,
  editorialRating: PropTypes.string,
  withAnchorLink: PropTypes.bool,
  pubStates: PropTypes.shape({}),
  creativeId: PropTypes.string,
  onRendered: PropTypes.func,
  trackActionStart: PropTypes.bool,
  actionStartTrackingData: PropTypes.shape({}),
};
